import { useQuery } from "@tanstack/react-query";

import useApiHelper from "../../hooks/useApiHelper";
import { IDefaultResponse } from "../../models/DefaultResponse";
import {
  IEbayAspectsResult,
  ITitleConfigCategorySummary,
  ITitleConfigResponse,
} from "../../models/Category/TitleConfig";
import { TitleConfigQueryKeys } from "./keys";

interface UseTitleConfigCategoriesOptions {
  searchTerm?: string;
  /** True for configured categories only, false for unconfigured only, undefined for all. */
  configured?: boolean;
  enabled?: boolean;
}

/**
 * Categories for the picker, the Add modal and the Duplicate modal. Each caller filters at the
 * API rather than pulling the whole category list and narrowing it in the browser.
 */
export function useTitleConfigCategories({
  searchTerm = "",
  configured,
  enabled = true,
}: UseTitleConfigCategoriesOptions = {}) {
  const { get } = useApiHelper();

  const params = new URLSearchParams({ searchTerm });
  if (configured !== undefined) params.set("configured", String(configured));

  const categories = useQuery({
    queryKey: TitleConfigQueryKeys.categories(searchTerm, configured),
    queryFn: () =>
      get<IDefaultResponse<ITitleConfigCategorySummary[]>>(
        `/part/categories/title-config?${params.toString()}`,
      ).then((res) => res.data),
    enabled,
  });

  return { categories };
}

/**
 * The configuration for one category. Categories that have never been configured come back with
 * seeded defaults and `isSaved: false`, so a direct link to one still renders.
 */
export function useTitleConfig(partCategoryId: string | undefined) {
  const { get } = useApiHelper();

  const config = useQuery({
    queryKey: TitleConfigQueryKeys.single(partCategoryId),
    queryFn: () =>
      get<IDefaultResponse<ITitleConfigResponse>>(
        `/part/categories/${partCategoryId}/title-config`,
      ).then((res) => res.data),
    enabled: !!partCategoryId,
  });

  return { config };
}

/**
 * The eBay item aspects for the category's mapped eBay category, feeding the aspect picker. Only
 * fetched while the picker is open - it is a live call out to eBay behind a server-side cache, and
 * most visits to the screen never open it.
 */
export function useEbayAspects(
  partCategoryId: string | undefined,
  enabled = true,
) {
  const { get } = useApiHelper();

  const aspects = useQuery({
    queryKey: TitleConfigQueryKeys.ebayAspects(partCategoryId),
    queryFn: () =>
      get<IDefaultResponse<IEbayAspectsResult>>(
        `/part/categories/${partCategoryId}/title-config/ebay-aspects`,
      ).then((res) => res.data),
    enabled: enabled && !!partCategoryId,
    // eBay taxonomy does not move within a session, and the API caches it for 12 hours anyway.
    staleTime: 1000 * 60 * 30,
  });

  return { aspects };
}
